import React, { useEffect, useMemo, useState } from 'react';

import { Button, PageBody, PageIntro, PageTitle, Paper, Tag } from 'src/components';
import {
  EMPTY_SELECTION,
  MAX_REPROCESS_ASSETS,
  OutputsPicker,
  parseAssetIds,
  ReviewSummary,
  selectionCount,
  StepIndicator,
  useReprocessFlow,
} from 'src/components/ReprocessFlow';
import useRoleManager from 'src/hooks/useRoleManager';
import WritesPausedBanner from 'src/components/FeatureUnavailable/WritesPausedBanner';

import flowStyles from 'src/components/ReprocessFlow/ReprocessFlow.module.scss';
import styles from './ReprocessAssets.module.scss';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const skippedText = (skipped: { id: string; reason: string }[]) =>
  skipped.map((s) => `${s.id} (${s.reason === 'not_found' ? 'not found' : 'no access'})`).join(', ');

/**
 * Admin / support entry point for batch reprocess (ui#1018): paste ids from a
 * ticket or a spreadsheet, pick outputs, review the cost, submit. The same
 * three-step flow runs as the "Reprocess…" bulk action on the asset grid,
 * which is where most people should start.
 */
const ReprocessAssets: React.FC = () => {
  const { isAdmin, isManager } = useRoleManager();
  const [idsInput, setIdsInput] = useState('');
  const parsed = useMemo(() => parseAssetIds(idsInput), [idsInput]);
  const overLimit = parsed.valid.length > MAX_REPROCESS_ASSETS;
  // Settle typing before asking the server about the ids.
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => {
    const next = overLimit ? [] : parsed.valid;
    const t = setTimeout(() => setIds((prev) => (prev.join() === next.join() ? prev : next)), 400);
    return () => clearTimeout(t);
  }, [overLimit, parsed.valid]);
  const flow = useReprocessFlow(ids);

  const reset = () => {
    setIdsInput('');
    flow.setSelection(EMPTY_SELECTION);
    flow.setStep('assets');
  };

  const handleSubmit = async () => {
    if (await flow.submit()) reset();
  };

  // The sidebar hides this page from other roles, but the URL is reachable,
  // and the backend refuses them anyway (saas#1262).
  if (!isAdmin && !isManager) {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Reprocess Assets" className={styles.pageTitle} />
        <PageBody narrow>
          <Paper>
            <p data-test="reprocess-not-permitted">Reprocessing is available to account Admins and Managers.</p>
          </Paper>
        </PageBody>
      </div>
    );
  }

  const probe = flow.probe.data;
  const settled = ids.join() === (overLimit ? '' : parsed.valid.join());
  const canLeaveAssets =
    settled && ids.length > 0 && parsed.invalid.length === 0 && !!flow.counts && !flow.probe.isFetching;

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Reprocess Assets" className={styles.pageTitle} />
      <PageIntro
        inset
        overview="Re-run conversions across assets you already hold, in bulk. Use it after changing a conversion preset, or when a batch needs an output it was never processed for. To reprocess what you are looking at, select assets in the grid and choose Reprocess… instead."
      />
      <PageBody narrow>
        <Paper>
          <div className={styles.flow}>
            <StepIndicator current={flow.step} />
            {flow.writesPaused && <WritesPausedBanner reason={flow.pausedReason} />}

            {flow.step === 'assets' && (
              <div className={styles.stepBody}>
                <label htmlFor="reprocess-asset-ids" className={styles.idsLabel}>
                  Asset IDs
                </label>
                <textarea
                  id="reprocess-asset-ids"
                  className={flowStyles.idsTextarea}
                  placeholder="Paste up to 100 asset IDs, separated by commas, spaces or new lines"
                  value={idsInput}
                  onChange={(e) => setIdsInput(e.target.value)}
                  data-test="reprocess-ids"
                />

                {idsInput.trim().length > 0 && (
                  <p className={flowStyles.hint} data-test="reprocess-ids-summary">
                    {plural(parsed.valid.length, 'valid ID')}
                    {parsed.duplicates > 0
                      ? `, ${parsed.duplicates} duplicate${parsed.duplicates === 1 ? '' : 's'} removed`
                      : ''}
                    {probe && flow.counts
                      ? `: ${plural(flow.counts.image, 'image')}, ${plural(flow.counts.video, 'video')}${
                          probe.counts.other ? `, ${plural(probe.counts.other, 'other file')}` : ''
                        }.`
                      : '.'}
                    {flow.probe.isFetching ? ' Checking…' : ''}
                  </p>
                )}

                {overLimit && (
                  <p className={flowStyles.error} role="alert">
                    {plural(parsed.valid.length, 'ID')} pasted. Up to {MAX_REPROCESS_ASSETS} can be
                    reprocessed at once.
                  </p>
                )}

                {parsed.invalid.length > 0 && (
                  <div className={flowStyles.error} role="alert" data-test="reprocess-invalid">
                    <div>
                      {parsed.invalid.length === 1 ? '1 entry is' : `${parsed.invalid.length} entries are`} not
                      an asset ID (32 hex characters).{' '}
                      <button
                        type="button"
                        className={flowStyles.linkButton}
                        onClick={() => setIdsInput(parsed.valid.join('\n'))}
                      >
                        Remove {parsed.invalid.length === 1 ? 'it' : 'them'}
                      </button>
                    </div>
                    <div className={flowStyles.chips}>
                      {parsed.invalid.slice(0, 20).map((t, i) => (
                        <Tag key={`${t}-${i}`} text={t} className={flowStyles.chipBad} />
                      ))}
                    </div>
                  </div>
                )}

                {probe && probe.skipped.length > 0 && (
                  <div className={flowStyles.warning} data-test="reprocess-probe-skipped">
                    {plural(probe.skipped.length, 'asset')} will be skipped: {skippedText(probe.skipped)}
                  </div>
                )}

                {flow.probe.error && (
                  <p className={flowStyles.error} role="alert">
                    {flow.probe.error.status === 403
                      ? 'You do not have permission to reprocess these assets.'
                      : flow.probe.error.message || 'Could not check these IDs.'}
                  </p>
                )}
              </div>
            )}

            {flow.step === 'outputs' && (
              <div className={styles.stepBody}>
                {flow.loadingSettings ? (
                  <p>Loading available outputs…</p>
                ) : (
                  <OutputsPicker groups={flow.groups} value={flow.selection} onChange={flow.setSelection} />
                )}
              </div>
            )}

            {flow.step === 'review' && (
              <div className={styles.stepBody}>
                <ReviewSummary
                  groups={flow.groups}
                  selection={flow.selection}
                  estimate={flow.estimate.data}
                  loading={flow.estimate.isLoading}
                  error={flow.estimate.error}
                />
              </div>
            )}

            <div className={styles.actions}>
              {flow.step !== 'assets' && (
                <Button
                  mode="flat"
                  size="small"
                  onClick={() => flow.setStep(flow.step === 'review' ? 'outputs' : 'assets')}
                  disabled={flow.submitting}
                >
                  Back
                </Button>
              )}
              {flow.step === 'assets' && (
                <Button
                  size="small"
                  onClick={() => flow.setStep('outputs')}
                  disabled={!canLeaveAssets}
                  data-test="reprocess-next-outputs"
                >
                  Next
                </Button>
              )}
              {flow.step === 'outputs' && (
                <Button
                  size="small"
                  onClick={() => flow.setStep('review')}
                  disabled={selectionCount(flow.selection) === 0}
                  data-test="reprocess-next-review"
                >
                  Review
                </Button>
              )}
              {flow.step === 'review' && (
                <Button
                  size="small"
                  onClick={handleSubmit}
                  loading={flow.submitting}
                  disabled={!flow.canSubmit || flow.estimate.isLoading}
                  data-test="reprocess-submit"
                >
                  Reprocess {plural(flow.submitIds.length, 'asset')}
                </Button>
              )}
            </div>
          </div>
        </Paper>
      </PageBody>
    </div>
  );
};

export default ReprocessAssets;
